import React from 'react';
import styles from './ServiceItem.module.scss';
import { salonManagementActions } from '../../../../store';
import { ROUTES } from '../../../../../../routes/constants';
import clx from 'classnames';
import HoveredInfoIcon from '../../../../../Common/HoveredInfoIcon';
import Switcher from '../../../../../Common/Switcher';
import ColorSelector from '../../../../../Common/CustomSelector/ColorSelector';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useWindowSize from '../../../../../../utils/useWindowSize';
import { useTranslation } from 'react-i18next';
import {
  Armchair,
  ChevronRight,
  GripVertical,
  Image,
  Pencil,
  UserCheck,
  UserX,
} from 'lucide-react';

export const ServiceItem = ({
  service,
  openEditServiceModal,
  colorsList,
  setServiceToDeleteId,
  setServiceToEnableId,
  isDragged,
  isOpen = false,
  onOpenChange,
  appPosition = null,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isMobile } = useWindowSize();

  const descDurataRaw = service.descDurata ?? service.desc_durata;
  const descDurataTrimmed = descDurataRaw == null ? '' : String(descDurataRaw).trim();
  const descDurataAsNum = Number(descDurataRaw);
  const hasDescDurata = descDurataTrimmed !== '';
  const appDurationDisplay =
    descDurataTrimmed !== '' && Number.isFinite(descDurataAsNum) && descDurataAsNum === 0
      ? service.duration
      : descDurataRaw;

  const costNumber = parseFloat(service.cost);
  const priceLabel = Number.isFinite(costNumber) ? `€ ${costNumber.toFixed(2)}` : '—';

  const closeCard = () => onOpenChange?.(false);

  return (
    <details
      className={clx(styles.item, { [styles.item_mobile]: isMobile })}
      open={isOpen}
      onToggle={(event) => {
        const nextOpen = event.currentTarget.open;
        if (nextOpen !== isOpen) onOpenChange?.(nextOpen);
      }}
    >
      <summary className={styles.item_summary}>
        <button
          type="button"
          data-movable-handle
          className={styles.drag_handle}
          style={{ cursor: isDragged ? 'grabbing' : 'grab' }}
          tabIndex={-1}
          aria-label={t('services.serviceItem.drag')}
          onClick={(event) => event.preventDefault()}
        >
          <GripVertical size={18} strokeWidth={2.2} />
        </button>

        {appPosition != null ? (
          <span className={styles.position_label} title={t('services.serviceItem.appPositionAria', { n: appPosition })}>
            {t('services.serviceItem.appPosition', { n: appPosition })}
          </span>
        ) : null}

        <span
          className={styles.color_dot}
          style={{ background: service.color || '#858AF9' }}
          aria-hidden="true"
        />

        <div className={styles.summary_copy}>
          <div className={styles.item_summary_name}>{service.name}</div>
          <div className={styles.summary_meta}>
            <span>
              {service.duration} {t('services.serviceItem.minutes')}
            </span>
            <span>{priceLabel}</span>
            {!!service.onlyVisibile && !service.disabled && (
              <span className={styles.meta_pill}>{t('services.serviceItem.notBookable')}</span>
            )}
            {!!service.disabled && (
              <span className={clx(styles.meta_pill, styles.meta_pill_disabled)}>
                {t('services.serviceItem.disabled')}
              </span>
            )}
          </div>
        </div>
        <div className={styles.item_summary_icon}>
          <ChevronRight size={18} strokeWidth={2.2} />
        </div>
      </summary>

      <div className={styles.item_container}>
        <div className={styles.info_card}>
          <div className={styles.info_row}>
            <span className={styles.info_label}>{t('services.serviceItem.serviceName')}</span>
            <div className={styles.info_value}>
              <span>{service.name}</span>
              <button
                type="button"
                className={styles.icon_action}
                onClick={() => openEditServiceModal(service)}
                aria-label={t('services.serviceItem.edit')}
              >
                <Pencil size={14} strokeWidth={2.2} />
              </button>
            </div>
          </div>
          <div className={styles.info_row}>
            <span className={styles.info_label}>{t('services.serviceItem.serviceDescription')}</span>
            <div className={styles.info_value}>
              <span>{service.description || '—'}</span>
              <button
                type="button"
                className={styles.icon_action}
                onClick={() => openEditServiceModal(service)}
                aria-label={t('services.serviceItem.edit')}
              >
                <Pencil size={14} strokeWidth={2.2} />
              </button>
            </div>
          </div>
          <div className={styles.info_row}>
            <span className={styles.info_label}>{t('services.serviceItem.duration')}</span>
            <div className={styles.info_value}>
              <span>
                {service.duration} {t('services.serviceItem.minutes')}
              </span>
              <button
                type="button"
                className={styles.icon_action}
                onClick={() => openEditServiceModal(service)}
                aria-label={t('services.serviceItem.edit')}
              >
                <Pencil size={14} strokeWidth={2.2} />
              </button>
            </div>
          </div>
          {hasDescDurata && (
            <div className={styles.info_row}>
              <span className={styles.info_label}>{t('services.serviceItem.appDuration')}</span>
              <div className={styles.info_value}>
                <span>
                  {appDurationDisplay} {t('services.serviceItem.minutes')}
                </span>
                <button
                  type="button"
                  className={styles.icon_action}
                  onClick={() => openEditServiceModal(service)}
                  aria-label={t('services.serviceItem.edit')}
                >
                  <Pencil size={14} strokeWidth={2.2} />
                </button>
              </div>
            </div>
          )}
          <div className={styles.info_row}>
            <span className={styles.info_label}>{t('services.serviceItem.servicePrice')}</span>
            <div className={styles.info_value}>
              <span>{priceLabel}</span>
              <button
                type="button"
                className={styles.icon_action}
                onClick={() => openEditServiceModal(service)}
                aria-label={t('services.serviceItem.edit')}
              >
                <Pencil size={14} strokeWidth={2.2} />
              </button>
            </div>
          </div>
        </div>

        <div className={styles.settings_row}>
          <div className={styles.setting_block}>
            <div className={styles.setting_title}>
              {t('services.serviceItem.bookableInApp')}
              <HoveredInfoIcon description={t('services.serviceItem.bookableInAppInfo')} />
            </div>
            <Switcher
              value={service.onlyVisibile}
              setValue={(value) => {
                closeCard();
                dispatch(
                  salonManagementActions.EDIT_SERVICE.REQUEST({
                    idServizio: service.id,
                    onlyVisibile: !+value,
                    setToAll: true,
                  }),
                );
              }}
              isFullWidth={isMobile}
            />
          </div>
          <div className={styles.setting_block}>
            <ColorSelector
              options={colorsList}
              value={service.color}
              setValue={(value) => {
                dispatch(
                  salonManagementActions.EDIT_SERVICE.REQUEST({
                    idServizio: service.id,
                    color: value,
                  }),
                );
              }}
              title={t('services.serviceItem.serviceColor')}
              isFullWidth={isMobile}
            />
          </div>
        </div>

        <div className={styles.nav_cards}>
          <button
            type="button"
            className={styles.nav_card}
            onClick={() =>
              navigate(`${ROUTES.SALON_MANAGEMENT}${ROUTES.SERVICES}${ROUTES.WORKERS}/${service.id}`)
            }
          >
            <span className={styles.nav_card_icon}>
              <Armchair size={18} strokeWidth={2.2} />
            </span>
            <span className={styles.nav_card_copy}>
              <span className={styles.nav_card_title}>{t('services.serviceItem.chairs')}</span>
              <span className={styles.nav_card_value}>
                {t('services.serviceItem.chairsDescription')}
              </span>
            </span>
            <ChevronRight size={18} strokeWidth={2.2} />
          </button>
          <button
            type="button"
            className={styles.nav_card}
            onClick={() =>
              navigate(`${ROUTES.SALON_MANAGEMENT}${ROUTES.SERVICES}${ROUTES.ICONS}/${service.id}`)
            }
          >
            <span className={styles.nav_card_icon}>
              <Image size={18} strokeWidth={2.2} />
            </span>
            <span className={styles.nav_card_copy}>
              <span className={styles.nav_card_title}>{t('services.serviceItem.icons')}</span>
              <span className={styles.nav_card_value}>
                {t('services.serviceItem.iconsDescription')}
              </span>
            </span>
            <ChevronRight size={18} strokeWidth={2.2} />
          </button>
        </div>

        <div className={styles.footer}>
          <div className={styles.footer_copy}>
            <div className={styles.footer_title_row}>
              <span className={styles.footer_title}>
                {service.disabled
                  ? t('services.serviceItem.enableService')
                  : t('services.serviceItem.disableService')}
              </span>
              {!service.disabled && (
                <HoveredInfoIcon description={t('services.serviceItem.disableServiceInfo')} />
              )}
            </div>
            {!service.disabled && (
              <p className={styles.footer_hint}>{t('services.serviceItem.disableHint')}</p>
            )}
          </div>
          {!service.disabled ? (
            <button
              type="button"
              onClick={() => {
                closeCard();
                setServiceToDeleteId(service.id);
              }}
              className={styles.delete_button}
            >
              <UserX size={16} strokeWidth={2.2} aria-hidden="true" />
              {t('common.actions.disable')}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                closeCard();
                setServiceToEnableId(service.id);
              }}
              className={styles.enable_button}
            >
              <UserCheck size={16} strokeWidth={2.2} aria-hidden="true" />
              {t('common.actions.enable')}
            </button>
          )}
        </div>
      </div>
    </details>
  );
};
